import * as THREE from "../../libs/three.js/build/three.module.js";

/**
 * 打洞式遮挡层：让 iframe（CSS3D，在 canvas 之下）能被点云/高斯逐像素遮挡，
 * 并支持整块网页按 opacity 与点云半透明混合。
 *
 * 原理：默认 framebuffer 在 render.pass.depth 时刻已持有点云/高斯的逐像素深度
 * （PotreeRenderer 直接写；EDL 的 edl.fs:67 / HQSplat 的 normalize.fs:24 用
 * gl_FragDepth 把深度搬回来；splat 有 depth-only 预 pass）。此时用一个
 * depthTest:true 的材质画盒子正面 quad，把通过深度测试的像素（= 盒子没被挡）
 * 的 canvas alpha 压到 1-opacity，浏览器合成时按这个 alpha 透出下层 iframe；
 * 深度测试失败的像素（= 被点云挡）根本不绘制 -> alpha 保持不透明 -> iframe 被遮住。
 *
 * ── 混合公式（uAlpha = 1 - opacity，opacity 为网页不透明度）──────────────
 * alpha 通道恒为「直接替换」：src=One, dst=Zero  ->  canvasA = uAlpha
 * RGB 通道 src 一律丢弃(Zero)，只决定 dst(点云颜色) 保留多少，取决于 canvas
 * 真实的 premultipliedAlpha（见 _premultiplied 的探测注释）：
 *   premultiplied=true : dst=SrcAlpha -> canvasRGB = 点云 × uAlpha
 *       浏览器合成 = canvasRGB + iframe×(1-canvasA) = 点云×(1-o) + iframe×o  ✓
 *   premultiplied=false: dst=One      -> canvasRGB = 点云（原色）
 *       浏览器合成 = canvasRGB×canvasA + iframe×(1-canvasA) = 点云×(1-o) + iframe×o  ✓
 * 两个端点自动退化正确，无需特殊分支：
 *   o=1 -> uAlpha=0，与旧的 NoBlending + vec4(0,0,0,0) 逐像素等价，纯网页
 *   o=0 -> uAlpha=1，canvas 完全不透明，网页彻底看不见
 * three.module.js:9458 `if (blending !== CustomBlending)` 表明 CustomBlending
 * 走用户给的 factor，不受 renderer.premultipliedAlpha 影响，所以这套公式可靠。
 * ─────────────────────────────────────────────────────────────────────
 *
 * 挂点：viewer.addEventListener("render.pass.depth")。必须在 VolumeTool.renderDepth
 * 之后（同一个事件，listener 按注册顺序，VolumeTool 先注册）、clearDepth 之前。
 * 三种 renderer 里该事件都满足「点云深度已进默认 FB、尚未 clearDepth、每帧一次」。
 *
 * 限制（已与用户确认接受）：
 *  - background 为 none/null 时清屏 alpha=0，天空区会透出 iframe（漏）。
 *    gradient/black/white（默认 gradient）下背景已刷成 alpha=1，不会漏。
 *  - WebGL 截图(makeScreenshot)拍不到 DOM -> 截图期间跳过打洞
 *  - canvas 在 iframe 之上会吃鼠标 -> mousemove 时按 CPU 射线动态切 canvas.pointerEvents
 *  - 洞边与 iframe 有 1-2px 对不齐（antialias:false，CSS3D 与 WebGL 投影亚像素差）
 *  - 半透明只作用于【整块网页】：CSS 层的 iframe 无法逐像素参与 WebGL 混合，
 *    做不到「网页内某些元素透、某些元素不透」
 *  - 多个网页盒前后重叠时逐个混合，重叠区颜色会有偏差
 *    （iframe 之间本来就只能按 DOM 序，属既有限制）
 *  - 胶囊控制条 renderOrder=999 > 打洞 998，始终盖在网页之上（有意如此）
 */

// RGB 全 0（会被 blendSrc=Zero 丢弃，值不参与结果），只有 alpha 有效
const HOLE_FRAGMENT = /* glsl */`
uniform float uAlpha;
void main() {
	gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha);
}
`;
const HOLE_VERTEX = /* glsl */`
void main() {
	gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export class HolePunchLayer {

	constructor(viewer) {
		this.viewer = viewer;
		/** @type {Map<THREE.Object3D, THREE.Mesh>} volume -> 打洞 quad mesh */
		this.entries = new Map();

		// 独立 Scene 持有打洞 quads，避免被其它 pass 的 scene 遍历误伤
		this.scene = new THREE.Scene();

		// 探测 canvas 真实的 premultipliedAlpha。
		// viewer.js 用 canvas.getContext('webgl2', attrs) 建 context 时并没有传
		// premultipliedAlpha（那一行是注释掉的），所以真实值是 WebGL 默认的 true；
		// 而 new THREE.WebGLRenderer({premultipliedAlpha:false}) 只影响 three 内部的
		// 预设混合公式，改不了已创建 context 的属性 —— 两者当前就是不一致的。
		// 浏览器合成 canvas 用的是【context 真实值】，所以必须问 context 本人，
		// 不能读 renderer.premultipliedAlpha，否则 RGB 的 blendDst 会选错、出现发白/光晕。
		let premultiplied = true;
		try {
			const gl = viewer.renderer.getContext();
			const attrs = gl && gl.getContextAttributes ? gl.getContextAttributes() : null;
			if (attrs) premultiplied = attrs.premultipliedAlpha !== false;
		} catch (e) {
			console.warn('[HolePunchLayer] 读取 contextAttributes 失败，按 premultipliedAlpha=true 处理', e);
		}
		this._premultiplied = premultiplied;

		// 复用临时对象
		this._tmpQuat = new THREE.Quaternion();
		this._tmpPos = new THREE.Vector3();
		this._tmpScale = new THREE.Vector3();
		this._tmpEuler = new THREE.Euler();

		// 与 CSS3DLayer 同一套贴面朝向（见 CSS3DLayer.js FRONT_QUAT 注释）
		this._frontQuat = new THREE.Quaternion().setFromEuler(
			new THREE.Euler(Math.PI / 2, Math.PI / 2, 0, 'XYZ')
		);

		viewer.addEventListener("render.pass.depth", this._onPassDepth.bind(this));

		// 鼠标穿透策略：不靠 readPixels（太脆弱 -- 后续 pass 可能覆写 alpha，且 mousedown 时机不确定），
		// 改为 CPU 射线检测：鼠标在某个 webInteractive=true 的网页盒正面范围内 -> canvas pointerEvents=none
		// 用 raycaster.intersectObject(box) 判断，box 是 YDBoxVolume 的盒子几何体，纯 CPU 开销极小。
		// 鼠标离开盒子范围 -> 恢复 canvas pointerEvents=auto。
		// 监听挂 window（canvas 被 pointerEvents=none 后仍需收事件来切回来）。
		this._canvasPE = 'auto';
		this._raycaster = new THREE.Raycaster();
		this._ndc = new THREE.Vector2();
		this._hit = [];
		this._onMouseMove = this._onMouseMove.bind(this);
		this._onMouseDown = this._onMouseDown.bind(this);
		window.addEventListener('mousemove', this._onMouseMove);
		window.addEventListener('mousedown', this._onMouseDown, true);  // capture：在 canvas 之前拿到
		this._dragging = false;

		// 截图标志：makeScreenshot 期间置 true，跳过打洞
		viewer.addEventListener('screenshot_start', () => { this._screenshot = true });
		viewer.addEventListener('screenshot_end', () => { this._screenshot = false });
	}

	/**
	 * 每个 entry 一份材质，而不是共享单例：不同网页盒可以有各自的透明度，
	 * 而共享材质的 uniforms 对象也是共享的 —— 一次 renderer.render(scene) 里
	 * 所有 mesh 都会读到同一个 uAlpha（最后一次赋值），做不到逐盒不同。
	 * shader 源码相同，three 的 program cache 会复用同一个 program，开销可忽略。
	 */
	_createHoleMaterial() {
		return new THREE.ShaderMaterial({
			uniforms: { uAlpha: { value: 0 } },
			vertexShader: HOLE_VERTEX,
			fragmentShader: HOLE_FRAGMENT,
			transparent: true,
			blending: THREE.CustomBlending,
			// alpha：直接替换为 src(=uAlpha)
			blendEquationAlpha: THREE.AddEquation,
			blendSrcAlpha: THREE.OneFactor,
			blendDstAlpha: THREE.ZeroFactor,
			// RGB：src 一律丢弃，只决定 dst(点云颜色) 保留多少（见文件头混合公式）
			blendEquation: THREE.AddEquation,
			blendSrc: THREE.ZeroFactor,
			blendDst: this._premultiplied ? THREE.SrcAlphaFactor : THREE.OneFactor,
			depthTest: true,
			depthWrite: false,
			colorWrite: true,
		});
	}

	/**
	 * 给一个 volume 注册打洞。quad 每帧从 volume.matrixWorld 同步位姿。
	 * 与 CSS3DLayer.attach 配对使用：CSS3DLayer.attach 挂 iframe，HolePunchLayer.attach 挂打洞 quad。
	 */
	attach(volume) {
		if (this.entries.has(volume)) return;
		const geo = new THREE.PlaneGeometry(1, 1);
		const mesh = new THREE.Mesh(geo, this._createHoleMaterial());
		mesh.frustumCulled = false;   // 位姿每帧变，且体积小，不走剔除
		mesh.renderOrder = 998;       // 在胶囊条(999)之前
		this.scene.add(mesh);
		this.entries.set(volume, mesh);
		// 用盒子当前的资源透明度初始化（覆盖 setRes({id:9, opacity}) 的路径）
		this.setOpacity(volume, volume.resOpacity === undefined ? 1 : volume.resOpacity);
	}

	detach(volume) {
		const mesh = this.entries.get(volume);
		if (!mesh) return;
		this.entries.delete(volume);
		this.scene.remove(mesh);
		mesh.geometry.dispose();
		mesh.material.dispose();
	}

	/**
	 * 设置该网页盒的不透明度。opacity: [0,1]，1=网页完全不透明（默认），0=网页完全看不见。
	 * 内部存成 uAlpha = 1 - opacity（canvas 该处要保留多少不透明度）。
	 */
	setOpacity(volume, opacity) {
		const mesh = this.entries.get(volume);
		if (!mesh) return;
		const value = Number(opacity);
		if (!isFinite(value)) return;
		const o = Math.min(1, Math.max(0, value));
		mesh.material.uniforms.uAlpha.value = 1 - o;
	}

	_onPassDepth(e) {
		if (this.entries.size === 0) return;
		if (this._screenshot) return;   // 截图期间不打洞

		const renderer = this.viewer.renderer;
		const camera = this.viewer.scene.getActiveCamera();

		// render.pass.depth 时 renderTarget 一定是 null（默认 framebuffer），无需切换
		for (const [volume, mesh] of this.entries) {
			// 隐藏或已销毁的网页盒不参与打洞。跳过 _syncQuad 还不够 —— quad 留在打洞
			// scene 里会沿用上一帧的矩阵继续把 canvas 像素 alpha 压掉，画面上出现一个
			// 没有盒子的透明破洞，所以必须同时关掉 quad 自身的 visible。
			const hidden = volume.visible === false || volume._disposed === true;
			mesh.visible = !hidden;
			if (hidden) continue;
			this._syncQuad(volume, mesh);
		}
		// 一次性渲染整个打洞 scene，比逐 mesh render 更高效
		renderer.render(this.scene, camera);
	}

	/**
	 * 同步打洞 quad 到 volume 正面（与 CSS3DLayer._syncEntry 同一套数学）。
	 * quad 是 1x1 PlaneGeometry，scale = (worldWidth, worldHeight, 1)。
	 */
	_syncQuad(volume, mesh) {
		volume.updateMatrixWorld();
		const worldWidth = Math.abs(volume.scale.y) || 1;
		const worldHeight = Math.abs(volume.scale.z) || 1;

		volume.matrixWorld.decompose(this._tmpPos, this._tmpQuat, this._tmpScale);
		mesh.position.copy(this._tmpPos);
		mesh.quaternion.copy(this._tmpQuat).multiply(this._frontQuat);
		// 与 iframe 同样的外推偏置，保证 quad 与 iframe 共面、洞与 iframe 对齐
		const offset = Math.abs(volume.scale.x) * 0.5 + worldWidth * 0.002;
		mesh.translateZ(offset);
		mesh.scale.set(worldWidth, worldHeight, 1);
		mesh.updateMatrixWorld();
	}

	/**
	 * 判断鼠标位置是否在某个可交互的网页盒正面范围内。
	 * 用 raycaster 对 volume.box 做射线检测（纯 CPU，开销极小）。
	 * 返回 true 时 canvas 应 pointerEvents=none 让穿透到 iframe。
	 * 注意与透明度无关：半透明的网页一样可交互，要关交互请用 volume.webInteractive。
	 */
	_shouldPierce(e) {
		if (this.entries.size === 0) return false;
		const ih = this.viewer.inputHandler;
		if (ih && ih.drag) return false;   // 拖拽中 Potree 接管

		const canvas = this.viewer.renderer.domElement;
		const rect = canvas.getBoundingClientRect();
		this._ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
		this._ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
		const camera = this.viewer.scene.getActiveCamera();
		this._raycaster.setFromCamera(this._ndc, camera);

		for (const [volume, mesh] of this.entries) {
			if (volume.webInteractive === false) continue;       // 业务关闭交互
			// 隐藏的盒子不该抢鼠标：不判这一条时，把网页盒 visible 设为 false 之后，
			// 鼠标移到它原来的位置仍会把 canvas 设成 pointerEvents:'none' 让穿透到
			// 一个已经不可见的 iframe，表现为 Potree 交互（轨道/选中）在某片区域失灵。
			if (volume.visible === false) continue;
			if (volume._disposed === true) continue;
			if (!volume.box) continue;
			this._hit.length = 0;
			volume.box.raycast(this._raycaster, this._hit);
			if (this._hit.length > 0) return true;
		}
		return false;
	}

	_onMouseMove(e) {
		this._setCanvasPE(this._shouldPierce(e) ? 'none' : 'auto');
	}

	// mousedown capture：点击前确保状态正确（用户停着不动再点击时 mousemove 不触发）
	_onMouseDown(e) {
		this._setCanvasPE(this._shouldPierce(e) ? 'none' : 'auto');
	}

	_setCanvasPE(pe) {
		if (pe === this._canvasPE) return;
		this._canvasPE = pe;
		this.viewer.renderer.domElement.style.pointerEvents = pe;
	}

	dispose() {
		// detach 内已 dispose 每个 entry 自己的 geometry 与 material
		for (const volume of Array.from(this.entries.keys())) this.detach(volume);
		window.removeEventListener('mousemove', this._onMouseMove);
		window.removeEventListener('mousedown', this._onMouseDown, true);
	}
}
